import { useState, useEffect, useCallback } from "react";
import { getMachines, getDashboardStats } from "../api/client";
import MachineCard from "../components/MachineCard";
import ArizaTestPaneli from "../components/ArizaTestPaneli";
import AlarmTable from "../components/AlarmTable";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Zap,
  RefreshCw,
  Wrench,
  Calendar,
  LogOut,
} from "lucide-react";

export default function Dashboard({
  onSelectMachine,
  onNavigate,
  user,
  onLogout,
}) {
  const [machines, setMachines] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const tick = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [machinesRes, statsRes] = await Promise.all([
        getMachines(),
        getDashboardStats(),
      ]);
      setMachines(machinesRes.data);
      setStats(statsRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Veri alınamadı:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Sistem verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "Toplam Makine",
      value: stats?.total_machines ?? 0,
      icon: <Zap className="w-6 h-6" />,
      color: "text-blue-400",
      bg: "bg-blue-500/10 border-blue-500/30",
    },
    {
      label: "Kritik",
      value: stats?.critical ?? 0,
      icon: <AlertTriangle className="w-6 h-6" />,
      color: "text-red-400",
      bg: "bg-red-500/10 border-red-500/30",
    },
    {
      label: "Uyarı",
      value: stats?.warning ?? 0,
      icon: <Activity className="w-6 h-6" />,
      color: "text-yellow-400",
      bg: "bg-yellow-500/10 border-yellow-500/30",
    },
    {
      label: "Normal",
      value: stats?.normal ?? 0,
      icon: <CheckCircle className="w-6 h-6" />,
      color: "text-green-400",
      bg: "bg-green-500/10 border-green-500/30",
    },
  ];

  const grouped = {
    BOYA: machines.filter((m) => m.hat_type === "BOYA"),
    ALKALI: machines.filter((m) => m.hat_type === "ALKALI"),
    TANDEM: machines.filter((m) => m.hat_type === "TANDEM"),
  };

  const hatLabels = {
    BOYA: "Boya Hattı",
    ALKALI: "Alkali Hat",
    TANDEM: "Tandem / PLTCM",
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-white leading-tight truncate">
                Kestirimci Bakım
              </h1>
              <p className="text-xs text-slate-400 hidden sm:block">
                Endüstriyel İş Zekası
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1.5">
              <div
                className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-green-400 animate-pulse" : "bg-slate-500"}`}
              />
              <span className="text-xs text-slate-400">
                {autoRefresh ? "Canlı" : "Durduruldu"}
              </span>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs px-2.5 py-1.5 rounded-lg border transition-all ${
                autoRefresh
                  ? "border-green-500/50 text-green-400 hover:bg-green-500/10"
                  : "border-slate-600 text-slate-400 hover:bg-slate-800"
              }`}
            >
              {autoRefresh ? "Durdur" : "Başlat"}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-slate-800 transition-all"
            >
              <RefreshCw className="w-3 h-3" />
              <span className="hidden sm:inline">Yenile</span>
            </button>
            <button
              onClick={() => onNavigate("workorders")}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-slate-800 transition-all"
            >
              <Wrench className="w-3 h-3" />
              <span className="hidden sm:inline">İş Emirleri</span>
            </button>
            <button
              onClick={() => onNavigate("maintenance")}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-slate-800 transition-all"
            >
              <Calendar className="w-3 h-3" />
              <span className="hidden sm:inline">Bakım Planı</span>
            </button>
            <span className="text-xs text-slate-500 font-mono">
              {clock.toLocaleTimeString("tr-TR")}
            </span>
            <div className="hidden sm:flex items-center gap-2 ml-2 pl-2 border-l border-slate-700">
              <span className="text-xs text-slate-400">
                {user?.full_name || user?.username}
              </span>
              <button
                onClick={onLogout}
                className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-red-900/30 hover:border-red-500/50 hover:text-red-400 transition-all"
              >
                <LogOut className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-screen-2xl mx-auto px-6 py-8">
        {/* İstatistik Kartları */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {statCards.map((s) => (
            <div key={s.label} className={`rounded-xl border p-5 ${s.bg}`}>
              <div className={`${s.color} mb-3`}>{s.icon}</div>
              <div className="text-3xl font-bold text-white mb-1">
                {s.value}
              </div>
              <div className="text-sm text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <ArizaTestPaneli onArizaChange={fetchData} />

        {/* Makine Kartları — Hat Bazlı */}
        {Object.entries(grouped).map(([hat, list]) => (
          <div key={hat} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-500 rounded-full" />
              <h2 className="text-lg font-semibold text-slate-200">
                {hatLabels[hat]}
              </h2>
              <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
                {list.length} makine
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth < 600
                    ? "1fr"
                    : window.innerWidth < 1024
                      ? "repeat(2, 1fr)"
                      : "repeat(3, 1fr)",
                gap: "1rem",
              }}
            >
              {list.map((m) => (
                <MachineCard key={m.id} machine={m} onClick={onSelectMachine} />
              ))}
            </div>
          </div>
        ))}

        {/* Alarm Geçmişi */}
        <div className="mt-8 bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-semibold text-slate-200">
              Son 24 Saat — Yüksek Risk Alarmları
            </h2>
            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
              {stats?.high_risk_events?.length ?? 0} alarm
            </span>
          </div>
          <AlarmTable events={stats?.high_risk_events ?? []} />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-600">
          Son 1 saatte {stats?.logs_last_hour ?? 0} sensör kaydı işlendi •{" "}
          <br />
          Model: XGBoost
          <br /> Ulus Hastürk | Bakırçay Üniversitesi | YBS
        </div>
      </div>
    </div>
  );
}
